import { ChangeDetectorRef, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { Component, OnInit, ViewEncapsulation, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { SearchFilterDataService } from '@appStore/metering-points/search-filter-data-service';
import { MeteringPointSettings, Choice, FilterDefinition, MeteringPointFilters, MeteringPointFilterSettings, MultiChoiceFilter } from '@model';
import { Store } from '@ngrx/store';
import * as fromAppStore from '@appStore/app';
import * as fromMeteringPointStore from '@appStore/metering-points';
import { TranslateService } from '@ngx-translate/core';
import { SxDialogService } from '@services';
import { take, filter, takeUntil } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { FilterType, MeteringType, Sidebar } from '@enums';
import { FilterPaneComponent } from '@core/containers';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-metering-points-filter',
    templateUrl: './metering-points-filter.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MeteringPointsFilterComponent implements OnInit, OnDestroy {
  @Input()
  filter: MeteringPointFilterSettings;
  @Input()
  tableView: boolean;
  @Input()
  isMobile: boolean;
  @Input()
  isExporting: boolean;
  @Input()
  paneType: MeteringType;

  @Output()
  meteringPointsFilterEvent = new EventEmitter<MeteringPointFilterSettings>();
  @Output()
  meteringPointsViewEvent = new EventEmitter<boolean>();
  @Output()
  export = new EventEmitter<MeteringPointFilterSettings>();

  filterActive = false
  searchForm: UntypedFormGroup;
  searchFilter: MeteringPointFilters;
  filterDefinitions: FilterDefinition[] = [];
  miscMultiChoiceFilter: MultiChoiceFilter;
  userMeteringPointFilterSettings: MeteringPointSettings;
  private subs = new SubSink();
  
constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private filterData: SearchFilterDataService,
    private translate: TranslateService,
    private meteringPointStore: Store<fromMeteringPointStore.State>,
    private appStore: Store<fromAppStore.State>,
    private sxDialogService: SxDialogService,
    private fb: UntypedFormBuilder) {
      this.loadSearchFilter();
  }

  loadSearchFilter() {
    this.subs.sink = this.filterData.currentSearchFilter
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(searchFilter => {
        this.searchFilter = searchFilter;
        this.createForm();
        this.setupListeners();
      });
  }

  createForm() {
    this.searchForm = this.fb.group({
      searchPhrase: ''
    });
  }

  setupListeners() {
    const searchPhraseListener$ = this.searchForm.controls['searchPhrase'].valueChanges;
    this.subs.sink = searchPhraseListener$.subscribe(searchPhrase =>
      this.doSearch((this.searchForm.value.searchPhrase = searchPhrase))
    );
  }

 doSearch(newValue: any) {
    this.searchFilter = {
      hotMeteringPointFilter: {
        searchPhrase: this.paneType === MeteringType.Hot ? 
          this.searchForm.value.searchPhrase : 
          this.searchFilter.hotMeteringPointFilter.searchPhrase,
        searchName: true,
        searchClientName: true,
        searchAddress: true,
        searchGroupName: true,
        searchId: true,
        meteringType: MeteringType.Hot
      },
      coldMeteringPointFilter: {
        searchPhrase: this.paneType === MeteringType.Cold ? 
          this.searchForm.value.searchPhrase : 
          this.searchFilter.coldMeteringPointFilter.searchPhrase,
        searchName: true,
        searchClientName: true,
        searchAddress: true,
        searchGroupName: true,
        searchId: true,
        meteringType: MeteringType.Cold
      }
    } as MeteringPointFilters;

    this.filterData.pushSearchFilter(this.searchFilter);
  }

  ngOnInit() {
    this.initializeSearchForm();
    this.initializeFilters();
    this.initializeFilterDefinitions();
  }
  
  ngOnChanges(changes: SimpleChanges){
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        let change = changes[propName];
          switch (propName) {
              
          case 'filter': {
            this.initializeFilters();
            this.setFilterDefinitions();
            break;
          }
        }
      }
    }
  }
    
  ngOnDestroy() {
    this.changeDetectorRef.detach();
    this.subs.unsubscribe();
  }

  initializeSearchForm() {
    this.searchForm.controls['searchPhrase'].setValue(this.paneType === MeteringType.Hot ? 
      this.searchFilter.hotMeteringPointFilter.searchPhrase : 
      this.searchFilter.coldMeteringPointFilter.searchPhrase);
  }

  initializeFilters() {
  
      if (!this.filter) {

        // Construct Filter with default values...
        this.filter = new MeteringPointFilterSettings();
        this.filter.miscMultiChoiceFilter = new MultiChoiceFilter();
        this.clearFilter();
      }
  
      this.initializeMiscFilter();
      this.changeDetectorRef.detectChanges();
  }

  initializeFilterDefinitions = () => {
  
    this.filterDefinitions  = [];
    
    if (this.filter.miscMultiChoiceFilter.selected) {
        // Övrigt (Logisk, Funktionskontroll, Intelligy...)
       let filterDefinition = new FilterDefinition();
       filterDefinition.name = 'MultiChoiceFilterPane';
       filterDefinition.filterId = 'Misc';
       filterDefinition.filterType = FilterType.MultiChoice;
       filterDefinition.label = this.translate.instant('meteringpoints.filter.misc');
       filterDefinition.data = [];
       filterDefinition.selected = this.filter.miscMultiChoiceFilter.selected;
       filterDefinition.multiChoiceFilter = {...this.filter.miscMultiChoiceFilter};
       this.filterDefinitions.push(filterDefinition);
    }
     
    this.filterActive = this.filterDefinitions.some(fd => fd.selected && fd.selected.length > 0);
    this.changeDetectorRef.detectChanges();
  }

  setFilterDefinitions = () => {

    this.filterDefinitions = [];
     
    // Övrigt (Logisk, Funktionskontroll, Intelligy...)
    let filterDefinition = new FilterDefinition();
    filterDefinition.name = 'MultiChoiceFilterPane';
    filterDefinition.filterId = 'Misc';
    filterDefinition.filterType = FilterType.MultiChoice;
    filterDefinition.label = this.translate.instant('meteringpoints.filter.misc.label');
    filterDefinition.data = [];
    filterDefinition.selected = this.filter.miscMultiChoiceFilter.selected;
    filterDefinition.multiChoiceFilter = {...this.filter.miscMultiChoiceFilter};
    this.filterDefinitions.push(filterDefinition);

    this.filterActive = this.filterDefinitions.some(fd => fd.selected && fd.selected.length > 0);
  }

  clearFilter() {
    // Clear all filter selections
    if (!this.filter) {
      return;
    }
  
    let data = <MeteringPointFilterSettings>{
      ...this.filter,
  
      miscMultiChoiceFilter: this.filter.miscMultiChoiceFilter
        ? {
          ...this.filter.miscMultiChoiceFilter,
          selected: null
        }
        : new MultiChoiceFilter(),
    };
  
    this.filterActive = false;
    this.meteringPointsFilterEvent.emit(<MeteringPointFilterSettings>data);
  }

  toggleView() {
    this.tableView = !this.tableView;
    this.meteringPointsViewEvent.emit(this.tableView);
  }

  onExport = () => {
    this.export.emit(this.filter);
  };

  removeFilter(filterDefinition: FilterDefinition, selection: string) {
    // Remove filter selection
  
    let data = <MeteringPointFilterSettings>{...this.filter};
  
    switch (filterDefinition.filterId) {
      case 'Misc':
        filterDefinition.selected = filterDefinition.selected.filter(x => x !== selection);
        filterDefinition.multiChoiceFilter.selected = filterDefinition.multiChoiceFilter.selected.filter(x => x !== selection);
        let _selectedChoices = this.filter.miscMultiChoiceFilter.selected.filter(x => x !== selection);
        data = <MeteringPointFilterSettings>{
          ...data,
          miscMultiChoiceFilter: {
            ...this.filter.miscMultiChoiceFilter,
            selected: _selectedChoices && _selectedChoices.length > 0 ? _selectedChoices : null
          }
        };
        break;
      default:
        return '';
    }
  
    this.filterActive = this.filterDefinitions.some(fd => fd.selected && fd.selected.length > 0);
    this.meteringPointsFilterEvent.emit(<MeteringPointFilterSettings>data);
  }
  
  configureFilter = () => {
      
    const subDestroy = new Subject<void>();
    this.setFilterDefinitions();
    
    let dialogRef = this.sxDialogService.openDialog(FilterPaneComponent, {definitions: this.filterDefinitions}, {
      panelClass: 'sx-filter-dialog'
    });
    this.appStore.dispatch(new fromAppStore.ActivateSidebar(Sidebar.FilterPanel));
    
    this.subs.sink = dialogRef
      .afterClosed()
      .pipe(takeUntil(subDestroy))
      .subscribe(result => {
            
        if (result) {
          this.filterActive = false;
    
          let data = <MeteringPointFilterSettings>{...this.filter};
            // Övrigt (Logisk, Funktionskontroll, Intelligy...)
            let miscFilter = result.filter(x => x.filterId === 'Misc');
            if (miscFilter) {
              data = <MeteringPointFilterSettings>{
                ...data,
                miscMultiChoiceFilter: {
                  ...this.filter.miscMultiChoiceFilter,
                  selected: miscFilter[0].selected
                }
              };
              this.filterActive = miscFilter[0].selected && miscFilter[0].selected.length > 0 ? true : this.filterActive;
            }
              
           this.meteringPointsFilterEvent.emit(<MeteringPointFilterSettings>data);
          }
          this.appStore.dispatch(new fromAppStore.InactivateSidebar(Sidebar.FilterPanel));
          subDestroy.next();
          subDestroy.complete();
        });
  };
  
  initializeMiscFilter() {
    if (!this.filter.miscMultiChoiceFilter) {
      let miscMultiChoiceFilter = new MultiChoiceFilter();
      let choices = this.getMiscChoices();
  
      if (choices && choices.length > 0) {
        miscMultiChoiceFilter.selections = choices;
      }
      // Add
      this.filter.miscMultiChoiceFilter = miscMultiChoiceFilter;
    } else {
      // Filter exists -> set selections
      let choices = this.getMiscChoices();

      // Update
      if (choices && choices.length > 0) {
        this.filter = <MeteringPointFilterSettings>{
          ...this.filter,
          miscMultiChoiceFilter: {
            ...this.filter.miscMultiChoiceFilter,
            selections: choices
          }
        };
      }
    }
  }

  getMiscChoices() {
    
    let choices = [];
    choices.push({
      name: this.translate.instant('meteringpoints.filter.misc.hasLogical'),
      value: this.translate.instant('meteringpoints.filter.misc.hasLogical'),
      selected: false
    } as Choice);
        
    choices.push({
      name: this.translate.instant('meteringpoints.filter.misc.hasFunctionControl'),
      value: this.translate.instant('meteringpoints.filter.misc.hasFunctionControl'),
      selected: false
    } as Choice);
  
    choices.push({
      name: this.translate.instant('meteringpoints.filter.misc.hasIntelligy'),
      value: this.translate.instant('meteringpoints.filter.misc.hasIntelligy'),
      selected: false
    } as Choice);
  
    return choices;
  }
}
